import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingCart, X, Mail, Calendar, MessageCircle, Send } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';

const CartSummary = () => {
  const { cartItems, removeFromCart, getTotalPrice, clearCart } = useCart();
  const [isOpen, setIsOpen] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    notes: ''
  });

  // Listen for cart open event from header
  useEffect(() => {
    const handleOpenCart = () => {
      setIsOpen(true);
    };
    
    window.addEventListener('openCart', handleOpenCart);
    return () => window.removeEventListener('openCart', handleOpenCart);
  }, []);

  const handleSubmitSelections = () => {
    if (cartItems.length === 0) {
      alert('Please select at least one service to continue.');
      return;
    }

    const servicesList = cartItems.map((item, index) => 
      `${index + 1}. ${item.name} - $${item.price}`
    ).join('%0A');

    const emailBody = `Hello,%0A%0AI'm interested in the following services from Apply Like A Pro™:%0A%0A${servicesList}%0A%0ATotal Investment: $${getTotalPrice()}%0A%0AContact Information:%0AName: ${formData.name}%0AEmail: ${formData.email}%0APhone: ${formData.phone || 'Not provided'}%0A%0AAdditional Notes:%0A${formData.notes || 'None'}%0A%0AThank you!`;

    window.location.href = `mailto:shelly@applylikeapro.org?subject=Let's Get Started&body=${emailBody}`;
  };

  const handleGetMoreInfo = () => {
    let emailBody = `Hello,%0A%0AI'd like to learn more about Apply Like A Pro™ services.%0A%0A`;
    
    // Include cart selections if any
    if (cartItems.length > 0) {
      const servicesList = cartItems.map((item, index) => 
        `${index + 1}. ${item.name} - $${item.price}`
      ).join('%0A');
      
      emailBody += `Services I'm interested in:%0A${servicesList}%0A%0ATotal Investment: $${getTotalPrice()}%0A%0A`;
    }
    
    emailBody += `Contact Information:%0AName: ${formData.name || 'To be provided'}%0AEmail: ${formData.email || 'To be provided'}%0APhone: ${formData.phone || 'To be provided'}%0A%0AQuestions/Notes:%0A${formData.notes || 'None'}%0A%0AThank you!`;

    window.location.href = `mailto:shelly@applylikeapro.org?subject=Let's Talk&body=${emailBody}`;
  };

  const handleScheduleCall = () => {
    // If there are cart items, send email with selections first
    if (cartItems.length > 0) {
      const servicesList = cartItems.map((item, index) => 
        `${index + 1}. ${item.name} - $${item.price}`
      ).join('%0A');
      
      let emailBody = `Hello,%0A%0AI'd like to schedule an intake call to discuss the following services:%0A%0A`;
      emailBody += `Services I'm interested in:%0A${servicesList}%0A%0ATotal Investment: $${getTotalPrice()}%0A%0A`;
      emailBody += `Contact Information:%0AName: ${formData.name || 'To be provided'}%0AEmail: ${formData.email || 'To be provided'}%0APhone: ${formData.phone || 'To be provided'}%0A%0A`;
      emailBody += `I'm scheduling a call via Calendly to discuss these services in detail.%0A%0AThank you!`;
      
      // Open email client with selections
      window.location.href = `mailto:shelly@applylikeapro.org?subject=Intake Call Request - Services Selected&body=${emailBody}`;
      
      // Give a moment for email client to open, then open Calendly
      setTimeout(() => {
        window.open('https://calendly.com/shelly-applylikeapro/30min', '_blank');
      }, 500);
    } else {
      // No selections, just open Calendly
      window.open('https://calendly.com/shelly-applylikeapro/30min', '_blank');
    }
  };

  return (
    <>
      {/* Floating Cart Button */}
      <motion.button
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 bg-gray-900 hover:bg-gray-800 text-white rounded-full p-4 shadow-2xl hover:shadow-3xl transition-all z-40 flex items-center space-x-2 border-2 border-gray-700"
      >
        <ShoppingCart size={24} />
        {cartItems.length > 0 && (
          <span className="absolute -top-2 -right-2 bg-warm-coral-500 text-white rounded-full w-7 h-7 flex items-center justify-center text-sm font-bold border-2 border-white">
            {cartItems.length}
          </span>
        )}
      </motion.button>

      {/* Cart Modal */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            />

            {/* Cart Panel */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              className="fixed right-0 top-0 h-full w-full max-w-lg bg-white shadow-2xl z-50 overflow-y-auto"
            >
              <div className="p-6">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-gray-900 font-serif-display">Your Selections</h2>
                  <button
                    onClick={() => setIsOpen(false)}
                    className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <X size={24} />
                  </button>
                </div>

                {/* Cart Items */}
                {cartItems.length === 0 ? (
                  <div className="text-center py-12">
                    <ShoppingCart size={64} className="mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-600 mb-6">No services selected yet</p>
                    <Button
                      onClick={() => setIsOpen(false)}
                      className="bg-brand-green-600 hover:bg-brand-green-700"
                    >
                      Browse Services
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="space-y-4 mb-6">
                      {cartItems.map((item) => (
                        <Card key={item.id} className="border-2">
                          <CardHeader className="pb-3">
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <CardTitle className="text-lg">{item.name}</CardTitle>
                                <CardDescription className="text-sm mt-1">
                                  {item.category}
                                </CardDescription>
                              </div>
                              <button
                                onClick={() => removeFromCart(item.id)}
                                className="p-1 rounded-lg hover:bg-red-50 text-red-600 transition-colors"
                              >
                                <X size={20} />
                              </button>
                            </div>
                          </CardHeader>
                          <CardFooter className="pt-0">
                            <p className="text-2xl font-bold text-brand-green-700">${item.price}</p>
                          </CardFooter>
                        </Card>
                      ))}
                    </div>

                    {/* Total */}
                    <div className="border-t-2 border-gray-200 pt-4 mb-6">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-lg font-semibold text-gray-900">Total Investment:</span>
                        <span className="text-3xl font-bold text-brand-green-700">${getTotalPrice()}</span>
                      </div>
                      <button
                        onClick={clearCart}
                        className="text-sm text-red-600 hover:text-red-700 underline"
                      >
                        Clear all selections
                      </button>
                    </div>

                    {/* Contact Form */}
                    {!showContactForm ? (
                      <Button
                        onClick={() => setShowContactForm(true)}
                        className="w-full bg-gradient-to-r from-brand-green-600 to-brand-green-700 hover:from-brand-green-700 hover:to-brand-green-800 text-white py-6 text-lg"
                      >
                        Continue to Contact Info
                      </Button>
                    ) : (
                      <div className="space-y-4 mb-6">
                        <div>
                          <Label htmlFor="name">Full Name *</Label>
                          <Input
                            id="name"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            placeholder="John Doe"
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="email">Email Address *</Label>
                          <Input
                            id="email"
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            placeholder="john@example.com"
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="phone">Phone Number (Optional)</Label>
                          <Input
                            id="phone"
                            type="tel"
                            value={formData.phone}
                            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                            placeholder="(555) 123-4567"
                          />
                        </div>
                        <div>
                          <Label htmlFor="notes">Additional Notes (Optional)</Label>
                          <Textarea
                            id="notes"
                            value={formData.notes}
                            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                            placeholder="Any specific requirements or questions?"
                            rows={3}
                          />
                        </div>

                        <Button
                          onClick={handleSubmitSelections}
                          disabled={!formData.name || !formData.email}
                          className="w-full bg-gradient-to-r from-warm-coral-500 to-warm-coral-600 hover:from-warm-coral-600 hover:to-warm-coral-700 text-white py-6 text-lg group"
                        >
                          <Send className="mr-2" size={20} />
                          Submit Selections
                        </Button>
                      </div>
                    )}
                  </>
                )}

                {/* Alternative Options */}
                <div className="border-t-2 border-gray-200 pt-6 mt-6 space-y-3">
                  <p className="text-sm text-gray-600 text-center mb-4">Or choose another option:</p>
                  
                  <Button
                    onClick={handleGetMoreInfo}
                    variant="outline"
                    className="w-full border-2 border-gray-300 hover:border-brand-green-600 hover:text-brand-green-600 py-4"
                  >
                    <MessageCircle className="mr-2" size={20} />
                    Get More Information
                  </Button>

                  <Button
                    onClick={handleScheduleCall}
                    variant="outline"
                    className="w-full border-2 border-gray-300 hover:border-warm-coral-500 hover:text-warm-coral-600 py-4"
                  >
                    <Calendar className="mr-2" size={20} />
                    Schedule Intake Call
                  </Button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default CartSummary;